import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import moment from 'moment-timezone';
import PropTypes from 'prop-types';

// WARNING: we may need to review the use of these constants if have to change
// the values significantly (the number 12 can cause issues, for example).
export const SALESFORCE_CHAT_START = 8; // 8am EST
export const SALESFORCE_CHAT_END = 20; // 8pm EST

const SESSION_LABEL = 'Messaging_Session';
const WEBSITE_LABEL = 'Authenticated Newsela';
const CHAT_ACTIVE_KEY = 'newsela_authenticated_support_chat_active';

const SALESFORCE_EVENTS = {
  WITHIN_BUSINESS_HOURS: 'onEmbeddedMessagingWithinBusinessHours',
  BUSINESS_HOURS_STARTED: 'onEmbeddedMessagingBusinessHoursStarted',
  BUSINESS_HOURS_ENDED: 'onEmbeddedMessagingBusinessHoursEnded',
  MESSAGING_READY: 'onEmbeddedMessagingReady',
  CONVERSATION_STARTED: 'onEmbeddedMessagingConversationStarted',
};

export const SalesforceChatSupportContext = createContext(undefined);

const SalesforceChatSupport = ({children}) => {
  const chatKey = window.SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_KEY || '';
  const siteEndpoint = window.SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_SITE_ENDPOINT || '';
  const scrt2Url = window.SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_SCRT2URL || '';
  const hasRequiredConfig = Boolean(chatKey && siteEndpoint && scrt2Url);

  const [isChatLoaded, setIsChatLoaded] = useState(false);
  const [isChatReady, setIsChatReady] = useState(false);
  const eventListenersRef = useRef([]);
  const bootstrapScriptRef = useRef(null);
  const initScriptRef = useRef(null);
  const pendingRestoreRef = useRef(false);

  const isOfficeHoursOverridden = new URLSearchParams(window.location.search).get('officeHours') === 'false';

  const cleanupEventListeners = useCallback(() => {
    eventListenersRef.current.forEach((cleanup) => cleanup());
    eventListenersRef.current = [];
  }, []);

  const isWithinOfficeHours = useCallback(() => {
    if (isOfficeHoursOverridden) return false;

    const now = moment().tz('America/New_York');
    const dayOfWeek = now.day();
    const currentHour = now.hour();
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const isDuringHours = currentHour >= SALESFORCE_CHAT_START && currentHour < SALESFORCE_CHAT_END;

    return isWeekday && isDuringHours;
  }, [isOfficeHoursOverridden]);

  useEffect(() => {
    if (!hasRequiredConfig) return;

    const cleanup = () => {
      bootstrapScriptRef.current?.remove();
      bootstrapScriptRef.current = null;
      initScriptRef.current?.remove();
      initScriptRef.current = null;

      cleanupEventListeners();

      document
        .querySelectorAll('[class*="embeddedMessaging"], [id*="embeddedMessaging"]')
        .forEach((el) => el.remove());

      sessionStorage.removeItem(CHAT_ACTIVE_KEY);

      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('ESW') ||
          key.startsWith('SFDC') ||
          key.includes('embeddedservice') ||
          key.includes('embedded_svc') ||
          key.includes('snapins') ||
          key.includes('scrt2')
        ) {
          localStorage.removeItem(key);
        }
      });

      delete window.embeddedservice_bootstrap;
      setIsChatLoaded(false);
      setIsChatReady(false);
    };

    cleanup();

    const bootstrapScript = document.createElement('script');
    bootstrapScript.type = 'text/javascript';
    bootstrapScript.src = `${siteEndpoint}/assets/js/bootstrap.min.js`;
    bootstrapScript.id = 'salesforce-bootstrap-script';

    bootstrapScript.onload = () => {
      const initScript = document.createElement('script');
      initScript.type = 'text/javascript';
      initScript.id = 'salesforce-init-script';
      initScript.innerHTML = `
        function initEmbeddedMessaging() {
          try {
            embeddedservice_bootstrap.settings.language = 'en_US';
            embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;
            embeddedservice_bootstrap.init(
              '${chatKey}',
              '${SESSION_LABEL}',
              '${siteEndpoint}',
              { scrt2URL: '${scrt2Url}' }
            );
          } catch (err) {
            console.error('Error loading Embedded Messaging: ', err);
          }
        }
        initEmbeddedMessaging();
      `;

      document.head.appendChild(initScript);
      initScriptRef.current = initScript;
      setIsChatLoaded(true);
    };

    bootstrapScript.onerror = (e) => {
      console.error('Failed to load Salesforce script:', e);
    };

    document.head.appendChild(bootstrapScript);
    bootstrapScriptRef.current = bootstrapScript;

    return cleanup;
  }, [hasRequiredConfig, chatKey, siteEndpoint, scrt2Url, cleanupEventListeners]);

  const launchChat = useCallback(() => {
    const bootstrap = window.embeddedservice_bootstrap;

    if (!bootstrap) {
      console.error('Salesforce embeddedservice_bootstrap is not defined');
      return;
    }

    if (!bootstrap.utilAPI) {
      console.error('Salesforce embeddedservice_bootstrap.utilAPI is not available');
      return;
    }

    bootstrap.utilAPI.launchChat().catch((error) => {
      console.error('Failed to launch Salesforce chat client:', error);
    });
  }, []);

  useEffect(() => {
    if (!isChatLoaded) return;

    const handleBusinessHoursActive = () => {
      setIsChatReady(!isOfficeHoursOverridden);
    };

    const handleBusinessHoursInactive = () => {
      setIsChatReady(false);
    };

    const handleMessagingReady = () => {
      window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields({
        Website: WEBSITE_LABEL,
      });

      if (!isWithinOfficeHours()) {
        return;
      }

      const hasActiveSession = sessionStorage.getItem(CHAT_ACTIVE_KEY);
      if (hasActiveSession) {
        sessionStorage.removeItem(CHAT_ACTIVE_KEY);
        pendingRestoreRef.current = true;
      }

      setIsChatReady(true);
    };

    const handleConversationStarted = () => {
      sessionStorage.setItem(CHAT_ACTIVE_KEY, 'true');
    };

    const eventHandlers = [
      [SALESFORCE_EVENTS.WITHIN_BUSINESS_HOURS, handleBusinessHoursActive],
      [SALESFORCE_EVENTS.BUSINESS_HOURS_STARTED, handleBusinessHoursActive],
      [SALESFORCE_EVENTS.BUSINESS_HOURS_ENDED, handleBusinessHoursInactive],
      [SALESFORCE_EVENTS.MESSAGING_READY, handleMessagingReady],
      [SALESFORCE_EVENTS.CONVERSATION_STARTED, handleConversationStarted],
    ];

    eventHandlers.forEach(([eventName, handler]) => {
      window.addEventListener(eventName, handler);
    });

    eventListenersRef.current = eventHandlers.map(([eventName, handler]) => {
      return () => window.removeEventListener(eventName, handler);
    });

    return cleanupEventListeners;
  }, [isChatLoaded, isOfficeHoursOverridden, isWithinOfficeHours, cleanupEventListeners]);

  useEffect(() => {
    if (isChatReady && pendingRestoreRef.current) {
      pendingRestoreRef.current = false;
      launchChat();
    }
  }, [isChatReady, launchChat]);

  const contextValue = useMemo(() => ({isChatReady, launchChat}), [isChatReady, launchChat]);

  return (
    <SalesforceChatSupportContext.Provider value={contextValue}>
      {children}
    </SalesforceChatSupportContext.Provider>
  );
};

SalesforceChatSupport.propTypes = {
  children: PropTypes.node,
};

SalesforceChatSupport.displayName = 'SalesforceChatSupport';

const useSalesforceChatSupport = () => {
  const context = useContext(SalesforceChatSupportContext);

  if (context === undefined) {
    throw new Error('useSalesforceChatSupport must be used within a SalesforceChatSupport provider');
  }

  return context;
};

export {SalesforceChatSupport, useSalesforceChatSupport};
